import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import Modal from "components/shared/modal/Modal";
import ProductPickerScrollArea from "components/pages/booking/articlesTab/productPicker/ProductPickerScrollArea";
import ProductPickerHeader from "components/pages/booking/articlesTab/productPicker/ProductPickerHeader";
import { debounce } from "lodash";
import useWindowSize from "helpers/useWindowSize";

import { useDatabaseReader } from "database/useDatabaseReader";
import useArticleSearchReader from "database/articles/useArticleSearchReader";
import useCatalogueWriter from "database/catalogues/useCatalogueWriter";
import { useArticleReader } from "database/articles/useArticleReader";
import {
  normalizeArticleEntries,
  extractArticleIDs,
  pickStampSeason,
  buildCatalogueEntry
} from "helpers/catalogueArticleHelpers";
import { buildProductGroups } from "helpers/catalogueSearchGrouping";
import ConfirmModal from "components/shared/ConfirmModal";
import SearchResultsPanel from "./searchResults/SearchResultsPanel";
import SearchStatusLine from "./searchResults/SearchStatusLine";
import { entryKey } from "./searchResults/SeasonChipRow";

const CatalogueProductPickerModal = ({
  show,
  onClose,
  catalogueID,
  isCustomMode,
  targetGroupArticleIDs,
  targetGroupName,
  otherSectionNamesByArticleID
}) => {
  //HOOKS
  const { t, i18n } = useTranslation();
  const searchBarRef = useRef();
  const { getCatalogueByID } = useDatabaseReader();
  const {
    searchByArticleNameOrNumberAllSeasons,
    retrieveAllRecordsForArticleNumbers,
    searchByCollectionKey,
    retrieveArticlesByIDs
  } = useArticleSearchReader();
  const { updateCatalogueArticleIDs } = useCatalogueWriter();
  const { activeSeasons, allSeasons } = useArticleReader();

  const { width } = useWindowSize();

  //STATE
  const [catalogue, setCatalogue] = useState();
  const [browsingSeason, setBrowsingSeason] = useState();
  const [searchResults, setSearchResults] = useState();
  const [articleSort, setArticleSort] = useState("collection");
  const [initialSelectedArticles, setInitialSelectedArticles] = useState();
  const [selectedArticles, setSelectedArticles] = useState();
  const [isSearching, setIsSearching] = useState(false);
  const searchSequenceRef = useRef(0);

  // In custom mode, track full catalogue articles separately from the
  // group-level visual selection so we can show + buttons for catalogue
  // articles that aren't yet in the target group.
  const fullCatalogueArticlesRef = useRef([]);
  // Parallel ref storing {id, seasonKey} entries for season-aware writes
  const catalogueEntriesRef = useRef([]);
  // Custom mode only: ids removed from the catalogue this session. Handed to the parent on close
  // so it can strip them from every section in ONE write. Doing it per-click would mean calling
  // the parent's onRemoveArticlesFromCatalogue, which computes its write from catalogueEntries
  // state that is stale mid-session — it would erase this session's adds.
  const removedFromCatalogueIdsRef = useRef(new Set());
  // { articleIDs, sectionNames } while a destructive removal awaits confirmation.
  const [pendingRemoval, setPendingRemoval] = useState(null);

  useEffect(() => {
    if (catalogueID == null) return;
    getCatalogueByID(catalogueID).then((catalogue) => {
      setCatalogue(catalogue);
      setBrowsingSeason(catalogue.seasonKey);
    });
  }, [catalogueID, getCatalogueByID]);

  //This should only run on first setup and then selected articles are managed
  //Initial selected articles should only be set once on initial setup
  useEffect(() => {
    if (catalogue == null || initialSelectedArticles != null) return;

    // Normalize entries and extract flat IDs for Typesense lookup
    const entries = normalizeArticleEntries(catalogue.articleIDs, catalogue.seasonKey);
    const uniqueIDs = extractArticleIDs(entries);

    retrieveArticlesByIDs(uniqueIDs).then((result) => {
      const allArticles = Object.values(result);

      if (isCustomMode) {
        // Store full catalogue articles in ref for Firestore writes
        fullCatalogueArticlesRef.current = allArticles;
        catalogueEntriesRef.current = entries;
        // Visual selection = only articles in the target group
        const targetSet = new Set(targetGroupArticleIDs || []);
        const groupArticles = allArticles.filter((a) => targetSet.has(a.id));
        setInitialSelectedArticles(groupArticles);
        setSelectedArticles(groupArticles);
      } else {
        catalogueEntriesRef.current = entries;
        setInitialSelectedArticles(allArticles);
        if (selectedArticles == null || selectedArticles?.length === 0) {
          setSelectedArticles(allArticles);
        }
      }
    });

    if (isCustomMode) {
      // Prevent effect from re-running while async fetch completes
      setInitialSelectedArticles([]);
    } else {
      setInitialSelectedArticles(catalogue.articles);
      if (selectedArticles == null || selectedArticles?.length === 0) {
        setSelectedArticles(catalogue.articles);
      }
    }
  }, [
    catalogue,
    initialSelectedArticles,
    retrieveArticlesByIDs,
    selectedArticles,
    isCustomMode,
    targetGroupArticleIDs
  ]);

  // One debounced function for the lifetime of the modal. Previously this was rebuilt on every
  // render, so a pending timer was never cancelled — and, more importantly, whichever response
  // landed last won regardless of which query was newest. Two-tier search makes that race routine:
  // type "pol" (exhaustive), then "o"; "polo" returns first and "pol" overwrites it.
  const runSearch = useCallback(
    async (searchTerm) => {
      if (searchTerm.length === 0) {
        searchSequenceRef.current += 1;
        setIsSearching(false);
        setSearchResults();
        return;
      }

      const sequence = searchSequenceRef.current + 1;
      searchSequenceRef.current = sequence;
      setIsSearching(true);

      const shallow = searchTerm.length < 3;

      try {
        const { records, truncated } = await searchByArticleNameOrNumberAllSeasons(
          searchTerm,
          { shallow }
        );
        if (sequence !== searchSequenceRef.current) return;

        let allRecords = records;
        // Only a truncated FULL search can be missing a sibling record. An untruncated result
        // already holds every record for every article number it matched, and preview must not pay
        // a second round-trip.
        if (!shallow && truncated) {
          const completion = await retrieveAllRecordsForArticleNumbers(
            records.map((record) => record.articleNumber)
          );
          if (sequence !== searchSequenceRef.current) return;
          if (completion.length > 0) {
            const seen = new Set(records.map((record) => record.id));
            allRecords = [
              ...records,
              ...completion.filter((record) => !seen.has(record.id))
            ];
          }
        }

        setSearchResults({
          mode: shallow ? "preview" : "full",
          truncated,
          term: searchTerm,
          failed: false,
          groups: buildProductGroups(allRecords, activeSeasons)
        });
      } catch (error) {
        console.log(error);
        if (sequence !== searchSequenceRef.current) return;
        setSearchResults({
          mode: shallow ? "preview" : "full",
          truncated: false,
          term: searchTerm,
          failed: true,
          groups: []
        });
      } finally {
        if (sequence === searchSequenceRef.current) setIsSearching(false);
      }
    },
    [
      activeSeasons,
      retrieveAllRecordsForArticleNumbers,
      searchByArticleNameOrNumberAllSeasons
    ]
  );

  const searchTermChanged = useMemo(() => debounce(runSearch, 350), [runSearch]);

  useEffect(() => () => searchTermChanged.cancel(), [searchTermChanged]);

  const onSortChange = (sort) => {
    setArticleSort(sort);
  };

  const onCloseProductPicker = () => {
    if (isCustomMode) {
      // Pass full catalogue articles + explicit group article IDs + entries to parent
      const allCatalogueArticles = fullCatalogueArticlesRef.current;
      const groupArticleIDs = selectedArticles
        ? selectedArticles.map((a) => a.id)
        : [];
      const entries = catalogueEntriesRef.current;
      const removedArticleIDs = [...removedFromCatalogueIdsRef.current];

      setCatalogue();
      setInitialSelectedArticles();
      setSelectedArticles();
      setBrowsingSeason();
      setSearchResults();
      fullCatalogueArticlesRef.current = [];
      catalogueEntriesRef.current = [];
      removedFromCatalogueIdsRef.current = new Set();

      onClose(allCatalogueArticles, groupArticleIDs, entries, removedArticleIDs);
      return;
    }

    const articlesOnClose = selectedArticles;
    const effectiveSeasonKey = browsingSeason || catalogue?.seasonKey;
    const entries = catalogueEntriesRef.current.length > 0
      ? catalogueEntriesRef.current
      : selectedArticles?.map((a) => ({
          id: a.id,
          seasonKey: pickStampSeason(a, allSeasons, effectiveSeasonKey)
        }));
    setCatalogue();
    setInitialSelectedArticles();
    setSelectedArticles();
    setBrowsingSeason();
    setSearchResults();
    catalogueEntriesRef.current = [];
    onClose(articlesOnClose, undefined, entries);
  };

  const onSeasonChange = useCallback((seasonKey) => {
    setBrowsingSeason(seasonKey);
  }, []);

  // Custom mode only. Removes the articles from the catalogue outright — the same meaning the
  // minus already carries in schema mode, and the same meaning as the section builder's Remove.
  // Sections are cleaned by the parent on close; see removedFromCatalogueIdsRef.
  const removeArticlesFromCatalogue = (articleIDs) => {
    const ids = new Set(articleIDs);

    setSelectedArticles((prev) => (prev || []).filter((a) => !ids.has(a.id)));
    fullCatalogueArticlesRef.current = fullCatalogueArticlesRef.current.filter(
      (a) => !ids.has(a.id)
    );
    const newEntries = catalogueEntriesRef.current.filter((e) => !ids.has(e.id));
    catalogueEntriesRef.current = newEntries;
    ids.forEach((id) => removedFromCatalogueIdsRef.current.add(id));

    updateCatalogueArticleIDs(newEntries, catalogue.id);
  };

  // Confirm when the rep cannot see what they are about to affect: the article also lives in
  // other sections, or it is a bulk removal. Otherwise remove immediately — undoing an add you
  // made three seconds ago should not need a dialog.
  const requestRemoval = (articleIDs, { alwaysConfirm = false } = {}) => {
    const sectionNames = [
      ...new Set(
        articleIDs.flatMap((id) => otherSectionNamesByArticleID?.get(id) || [])
      )
    ];
    if (alwaysConfirm || sectionNames.length > 0) {
      setPendingRemoval({ articleIDs, sectionNames });
      return;
    }
    removeArticlesFromCatalogue(articleIDs);
  };

  //Single Article Selection Toggle. `chosenSeasonKey` is the chip the rep clicked; it wins over
  //pickStampSeason, which would otherwise return the record's newest season and override the choice.
  const onArticleSelection = (selectedArticle, chosenSeasonKey) => {
    const effectiveSeasonKey = browsingSeason || catalogue?.seasonKey;

    if (isCustomMode) {
      // Already in the group → this click is a remove.
      if (selectedArticles?.some((a) => a.id === selectedArticle.id)) {
        requestRemoval([selectedArticle.id]);
        return;
      }

      setSelectedArticles((prev) => [...(prev || []), selectedArticle]);

      if (
        !fullCatalogueArticlesRef.current.some((a) => a.id === selectedArticle.id)
      ) {
        fullCatalogueArticlesRef.current = [
          ...fullCatalogueArticlesRef.current,
          selectedArticle
        ];
        catalogueEntriesRef.current = [
          ...catalogueEntriesRef.current,
          buildCatalogueEntry(
            selectedArticle,
            allSeasons,
            chosenSeasonKey,
            effectiveSeasonKey
          )
        ];
        updateCatalogueArticleIDs(catalogueEntriesRef.current, catalogue.id);
      }
      return;
    }

    let articlesToWrite = [];
    if (selectedArticles.some((article) => article.id === selectedArticle.id)) {
      articlesToWrite = selectedArticles.filter(
        (article) => article.id !== selectedArticle.id
      );
    } else {
      articlesToWrite = [...selectedArticles, selectedArticle];
    }
    setSelectedArticles(articlesToWrite);

    const newEntries = articlesToWrite.map((article) => {
      const existing = catalogueEntriesRef.current.find((e) => e.id === article.id);
      if (existing) return existing;
      return buildCatalogueEntry(
        article,
        allSeasons,
        article.id === selectedArticle.id ? chosenSeasonKey : undefined,
        effectiveSeasonKey
      );
    });
    catalogueEntriesRef.current = newEntries;
    updateCatalogueArticleIDs(newEntries, catalogue.id);
  };

  //Articles Addition and Subtraction from Catalogue
  const onAddArticlesFromGroup = async (groupKey) => {
    const effectiveSeasonKey = browsingSeason || catalogue?.seasonKey;
    const result = await searchByCollectionKey(effectiveSeasonKey, groupKey);
    const newArticles = Object.values(result);

    if (isCustomMode) {
      // Add articles to group selection (that aren't already there)
      const current = selectedArticles || [];
      let updatedGroup = [...current];
      newArticles.forEach((article) => {
        if (!current.some((a) => a.id === article.id)) {
          updatedGroup.push(article);
        }
      });
      setSelectedArticles(updatedGroup);

      // Add to catalogue ref if not already there
      let catalogueUpdated = false;
      newArticles.forEach((article) => {
        if (
          !fullCatalogueArticlesRef.current.some((a) => a.id === article.id)
        ) {
          fullCatalogueArticlesRef.current.push(article);
          catalogueEntriesRef.current.push({
            id: article.id,
            seasonKey: pickStampSeason(article, allSeasons, effectiveSeasonKey)
          });
          catalogueUpdated = true;
        }
      });
      if (catalogueUpdated) {
        updateCatalogueArticleIDs(
          catalogueEntriesRef.current,
          catalogue.id
        );
      }
      return;
    }

    let newSelectedArticles = [...selectedArticles];
    newArticles.forEach((newArticle) => {
      if (!selectedArticles.some((article) => article.id === newArticle.id)) {
        newSelectedArticles.push(newArticle);
      }
    });
    setSelectedArticles(newSelectedArticles);
    const newEntries = newSelectedArticles.map((article) => ({
      id: article.id,
      seasonKey:
        catalogueEntriesRef.current.find((e) => e.id === article.id)?.seasonKey ||
        pickStampSeason(article, allSeasons, effectiveSeasonKey)
    }));
    catalogueEntriesRef.current = newEntries;
    updateCatalogueArticleIDs(newEntries, catalogue.id);
  };

  const onRemoveArticlesFromCollection = async (collectionKey) => {
    const effectiveSeasonKey = browsingSeason || catalogue?.seasonKey;
    const isInCollection = (article) =>
      article[`${effectiveSeasonKey}_collectionKeys`]?.some((item) =>
        item.includes(collectionKey)
      );

    if (isCustomMode) {
      // Acts on the WHOLE catalogue, not just the group being edited — the sidebar's collections
      // are schema categories and have no relationship to the custom section tree.
      // Only matches articles carrying the browsed season's collection keys, so a catalogue item
      // from another season is left alone. Pre-existing behaviour of this filter.
      const idsToRemove = fullCatalogueArticlesRef.current
        .filter(isInCollection)
        .map((a) => a.id);
      if (idsToRemove.length === 0) return;
      requestRemoval(idsToRemove, { alwaysConfirm: true });
      return;
    }

    const newSelectedArticles = selectedArticles.filter(
      (article) => !isInCollection(article)
    );

    setSelectedArticles(newSelectedArticles);
    const removedIds = new Set(
      selectedArticles.filter((a) => !newSelectedArticles.includes(a)).map((a) => a.id)
    );
    const newEntries = catalogueEntriesRef.current.filter((e) => !removedIds.has(e.id));
    catalogueEntriesRef.current = newEntries;
    updateCatalogueArticleIDs(newEntries, catalogue.id);
  };

  // Which {record, season} pairs are already in the catalogue, so a chip can say "added" about its
  // own season rather than about its record. Read from the entries ref (not selectedArticles, which
  // holds records and carries no season): every add/remove sets the ref and then setSelectedArticles,
  // so a re-render always sees the current entries.
  const addedEntryKeys = useMemo(
    () =>
      new Set(
        catalogueEntriesRef.current.map((entry) =>
          entryKey(entry.id, entry.seasonKey)
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedArticles]
  );

  const searchStatus = useMemo(() => {
    if (searchResults == null) return null;
    if (isSearching) return { kind: "searching" };
    if (searchResults.failed) return { kind: "error" };
    if (searchResults.groups.length === 0)
      return { kind: "empty", term: searchResults.term };
    if (searchResults.mode === "preview") return { kind: "preview" };
    if (searchResults.truncated) return { kind: "truncated" };
    return { kind: "normal", count: searchResults.groups.length };
  }, [searchResults, isSearching]);

  // escapeValue:false because React escapes on render anyway, and services/i18n.js sets no
  // interpolation config — a section named "Rain / Wind" would otherwise reach the rep as
  // "Rain &#x2F; Wind". {{total}}, never {{count}}: i18next treats `count` as a plural trigger.
  const buildRemovalMessage = () => {
    if (pendingRemoval == null) return "";
    const { articleIDs, sectionNames } = pendingRemoval;
    const isBulk = articleIDs.length > 1;

    const lead = isBulk
      ? t(
          "confirm_remove_bulk",
          "Remove {{total}} products from this catalogue? They can be re-added later.",
          { total: articleIDs.length }
        )
      : t(
          "confirm_remove_single",
          "Remove this product from the catalogue? It can be re-added later."
        );

    if (sectionNames.length === 0) return lead;

    const alsoIn = isBulk
      ? t(
          "confirm_remove_bulk_other_sections",
          "Some are also in {{sections}} and will be removed from there too.",
          { sections: sectionNames.join(", "), interpolation: { escapeValue: false } }
        )
      : t(
          "confirm_remove_other_sections",
          "It is also in {{sections}} and will be removed from there too.",
          { sections: sectionNames.join(", "), interpolation: { escapeValue: false } }
        );

    return `${lead} ${alsoIn}`;
  };

  return (
    <>
    <Modal
      title={isCustomMode && targetGroupName
        ? `${t("article_selection")} — ${targetGroupName}`
        : t("article_selection")}
      show={show}
      onClose={onCloseProductPicker}
      maxHeight={"88vh"}
      width={width > 1350 ? "1300px" : "96vw"}
    >
      <Container>
        <ProductPickerHeader
          searchBarRef={searchBarRef}
          searchTermChanged={searchTermChanged}
          onSortChange={onSortChange}
          enableCartButton={false}
          enableSaveButton={true}
          showOrderPicker={false}
          selectedArticles={selectedArticles}
          onSaveButtonPressed={onCloseProductPicker}
          showItemCount={true}
        />
        <ProductPickerScrollArea
          allowMultipleSeasons={true}
          allowOrderEntry={false}
          searchResults={searchResults}
          selectedArticles={selectedArticles}
          articleSort={articleSort}
          onArticleSelection={onArticleSelection}
          onAddArticlesFromGroup={onAddArticlesFromGroup}
          onRemoveArticlesFromCollection={onRemoveArticlesFromCollection}
          showCollectionHeader={true}
          showInventoryButton={true}
          selectedSeasonKey={browsingSeason || catalogue?.seasonKey}
          onSeasonChange={onSeasonChange}
          searchStatus={<SearchStatusLine status={searchStatus} />}
          renderSearchResults={({ onZoomArticle, onViewInventory }) => (
            <SearchResultsPanel
              groups={searchResults?.groups}
              isSearching={isSearching}
              isTruncated={searchResults?.truncated}
              selectedArticles={selectedArticles}
              showInventoryButton={true}
              onZoomArticle={onZoomArticle}
              onViewInventory={onViewInventory}
              onArticleSelection={onArticleSelection}
              addedEntryKeys={addedEntryKeys}
              language={i18n.language}
            />
          )}
        />
      </Container>
    </Modal>
    <ConfirmModal
      show={pendingRemoval != null}
      onClose={() => setPendingRemoval(null)}
      title={t("remove_from_catalogue", "Remove from Catalogue")}
      message={buildRemovalMessage()}
      isDestructive
      buttonTitle={t("remove", "Remove")}
      onConfirm={() => {
        removeArticlesFromCatalogue(pendingRemoval.articleIDs);
        setPendingRemoval(null);
      }}
    />
    </>
  );
};

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr;
  gap: 10px 0;
  width: 100%;
  height: 100%;
  padding: 10px;
  overflow-y: hidden;
`;

export default CatalogueProductPickerModal;
